import React, { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Slid from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BASE_URL } from "./Config/Config";
import Loader from "./Loader/Loader";
import { useQuery } from "@tanstack/react-query";

function Slider() {
  const location = useLocation();

  // -------------------------
  // FETCH FUNCTION FOR REACT QUERY
  // -------------------------
  const fetchSlider = async () => {
    const res = await fetch(`${BASE_URL}/homeslidemaster/`);
    if (!res.ok) throw new Error("Failed to fetch slider data");
    const json = await res.json();
    return json.data?.slice(0, 3) ?? [];
  };

  // -------------------------
  // USEQUERY
  // -------------------------
  const {
    data: homeSlides = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["home-slides"],
    queryFn: fetchSlider,
    staleTime: 1000 * 60 * 5, // 5 min cache
  });

  // -------------------------
  // BACKGROUND + MASK HANDLING
  // -------------------------
  useEffect(() => {
    const bgSrcElements = document.querySelectorAll("[data-bg-src]");
    bgSrcElements.forEach((el) => {
      const src = el.getAttribute("data-bg-src");
      if (src) {
        el.style.backgroundImage = `url(${src})`;
        el.classList.add("background-image");
        el.removeAttribute("data-bg-src");
      }
    });

    const bgColorElements = document.querySelectorAll("[data-bg-color]");
    bgColorElements.forEach((el) => {
      const color = el.getAttribute("data-bg-color");
      if (color) {
        el.style.backgroundColor = color;
        el.removeAttribute("data-bg-color");
      }
    });

    const maskSrcElements = document.querySelectorAll("[data-mask-src]");
    maskSrcElements.forEach((el) => {
      const mask = el.getAttribute("data-mask-src");
      if (mask) {
        el.style.maskImage = `url(${mask})`;
        el.style.WebkitMaskImage = `url(${mask})`;
        el.classList.add("bg-mask");
        el.removeAttribute("data-mask-src");
      }
    });
  }, [location.pathname]);

  // -------------------------
  // SLICK SETTINGS
  // -------------------------
  const settings = {
    dots: true,
    customPaging: () => (
      <button
        style={{
          width: "12px",
          height: "12px",
          backgroundColor: "gray",
          borderRadius: "50%",
          border: "none",
        }}
      ></button>
    ),
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 600,
    fade: true,
  };

  // -------------------------
  // LOADING / ERROR UI
  // -------------------------
  if (isLoading) return <Loader />;

  if (isError)
    return (
      <div className="text-center text-danger py-5">
        Failed to load slider data. Please refresh.
      </div>
    );

  // -------------------------
  // MAIN UI
  // -------------------------
  return (
    <div className="th-hero-wrapper hero-1" id="hero">
      <Slid
        {...settings}
        className="hero-slider-1 th-carousel"
        data-fade="true"
        data-dots="true"
      >
        {homeSlides.map((data, index) => (
          <div key={index} className="th-hero-slide">
            <div
              className="th-hero-bg"
              data-overlay="title"
              data-opacity={6}
              style={{
                backgroundImage: `url(${BASE_URL}${data.slider_img_bg})`,
              }}
            />

            <div className="container">
              <div className="row align-items-center justify-content-center">
                {/* LEFT SIDE CONTENT */}
                <div className="col-md-6">
                  <div className="hero-style1">
                    <span
                      className="hero-subtitle"
                      data-ani="slideinleft"
                      data-ani-delay="0.1s"
                    >
                      <span>{data.offer_off_button}</span> {data.first_line}
                    </span>

                    <h1
                      className="hero-title text-white"
                      data-ani="slideinleft"
                      data-ani-delay="0.4s"
                    >
                      {data.header_text}{" "}
                      <span className="text-theme">and Success</span>
                    </h1>

                    <p
                      className="hero-text"
                      data-ani="slideinleft"
                      data-ani-delay="0.6s"
                    >
                      {data.description}
                    </p>

                    <div
                      className="btn-group"
                      data-ani="slideinleft"
                      data-ani-delay="0.8s"
                    >
                      <NavLink
                        to={data.get_more_link}
                        className="th-btn style3"
                      >
                        Get Started<i className="fas fa-arrow-right ms-2" />
                      </NavLink>
                    </div>
                  </div>
                </div>

                {/* RIGHT SIDE IMAGE */}
                <div className="col-md-6 text-lg-end text-center">
                  <div className="hero-img1">
                    <img
                      src={`${BASE_URL}${data.slider_img}`}
                      alt="Hero Visual"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* DECORATIONS */}
            <div className="hero-shape shape1">
              <img src="assets/img/hero/shape_1_1.png" alt="shape" />
            </div>
            <div className="hero-shape shape2">
              <img src="assets/img/hero/shape_1_2.png" alt="shape" />
            </div>
            <div className="hero-shape shape3">
              <img src="assets/img/hero/shape_2_4.png" alt="shape" />
            </div>
            <div
              className="hero-shape shape4 shape-mockup jump-reverse"
              data-right="3%"
              data-bottom="7%"
            >
              <img src="assets/img/hero/shape_1_3.png" alt="shape" />
            </div>
            <div
              className="hero-shape shape5 shape-mockup jump-reverse"
              data-left="0"
              data-bottom="0"
            >
              <img src="assets/img/hero/shape_1_4.png" alt="shape" />
            </div>
          </div>
        ))}
      </Slid>
    </div>
  );
}

export default Slider;
